import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useCart, useSessionCustomer } from '../../customer';
import { usePet, usePets } from '../../pets';
import { EMPTY_PET } from '../../pets/constants';
import { Redirect } from '../../router';
import { useOffer } from '../../offer';
import { useToast } from '../../Toast';
import { StripeElementsProvider } from '../../payment';
import {
  RedirectToPathWithoutPetId,
  QUIZ_SUBPATH,
  MEALPREP_SELECTION_SUBPATH,
  RECIPES_SELECTION_SUBPATH,
  CHECKOUT_SUBPATH,
  CONFIRMATION_SUBPATH,
  RedirectToPathWithPetId,
  ALLERGIES_SLUG,
  ONBOARDING_PATH,
} from '../routing';
import { onboardingBackButtonClicked } from '../events';
import { PrefixedReporterProvider, useReporter } from '../../reporter';
import { QuizContainer } from '../QuizContainer';
import { PlanSelectionContainer } from '../PlanSelectionContainer';
import { CheckoutContainer } from '../CheckoutContainer';
import { ApiResult, isNotFoundError } from '../../api';
import { translateContentToProps } from '../translation';
import { LoadingView } from '../../components';
import { GetStartedContainer } from '../GetStartedContainer';
import { CheckoutConfirmationContainer } from '../CheckoutConfirmationContainer';
import { getSteps } from './steps';
import {
  calcCurrentStep,
  allStepsValid,
  allPetsHaveProductInCart,
  areAllStepsComplete,
  getFirstPetWithIncompleteSteps,
  getFirstPetWithNoProductInCart,
} from './guard';

export const OnboardingContainer = ({ content }) => {
  const { showToast } = useToast();
  const reporter = useReporter();
  const router = useRouter();
  const offer = useOffer();

  const [subpath, slug] = router.query.path ?? [];

  const { status } = useSession();
  const isSessionLoading = status === 'loading';
  const customerApi = useSessionCustomer();
  const petsApi = usePets();
  const petId = /** @type {string} */ (router.query.pet_id);
  const petApi = usePet(petId);
  const cartApi = useCart();

  const steps = getSteps();
  const data = {
    customer: customerApi.customer,
    pet: petApi.pet,
  };

  const getCurrentStep = () => {
    const currentIndex = slug
      ? steps.findIndex(step => step.slug === slug)
      : steps.length - 1;
    return calcCurrentStep(currentIndex, data, steps, content);
  };

  const getNextStepPath = useCallback(
    ({ currentId, currentSlug }) => {
      const currentIndex = steps.findIndex(step => step.slug === currentSlug);
      const isLastStep = steps[steps.length - 1].slug === currentSlug;
      if (!isLastStep) {
        return `/onboarding/quiz/${
          steps[currentIndex + 1].slug
        }/?pet_id=${currentId}`;
      }
      return `/onboarding/${MEALPREP_SELECTION_SUBPATH}/?pet_id=${currentId}`;
    },
    [steps],
  );

  const getPageonInitialFlow = () => {
    if (isLoading) return null;

    if (
      // if entering flow with customer having products for every pet then redirect to checkout
      petId == null &&
      petsApi.pets &&
      petsApi.pets.length &&
      cartApi.cart?.line_items.length &&
      allPetsHaveProductInCart(petsApi.pets, cartApi.cart)
    ) {
      return `/onboarding/${CHECKOUT_SUBPATH}/`;
    }

    if (allStepsValid(data, steps)) {
      // if the customer doesn't have a product but has completed all of the steps then go to plan selection otherwise go to proper step in quiz
      return `/onboarding/${MEALPREP_SELECTION_SUBPATH}/?pet_id=${petId ?? ''}`;
    } else {
      return `/onboarding/${QUIZ_SUBPATH}/?pet_id=${petId ?? ''}`;
    }
  };

  useEffect(() => {
    router.prefetch(getNextStepPath({ currentId: petId, currentSlug: slug }));
  }, [router, getNextStepPath, petId, slug]);

  const skipInterstitialRef = useRef(false);

  useEffect(() => {
    const trackPreviousUrl = nextPath => {
      const currentPath = router.asPath;
      // If we're going from Checkout -> Plan Selection
      // we should skip the interstitial. Using a ref
      // ensures we don't trigger unnecessary renders.
      skipInterstitialRef.current =
        currentPath.includes(CHECKOUT_SUBPATH) &&
        nextPath.includes(MEALPREP_SELECTION_SUBPATH);
    };

    router.events.on('beforeHistoryChange', trackPreviousUrl);

    return () => router.events.off('beforeHistoryChange', trackPreviousUrl);
  }, [router]);

  const isLoading =
    isSessionLoading ||
    // If the customer is not authenticated, these will "perma-load".
    (customerApi.isAuthenticated &&
      (customerApi.isLoading ||
        petApi.isLoading ||
        petsApi.isLoading ||
        cartApi.isLoading));
  // Note that `next/router` doesn't have the query string on initial render.
  // This is because the app is statically generated without the query string,
  // and Next does not provide the query string on initial render in order to
  // ensure the HTML output matches between server & client on initial render.
  // This is ok, because the customer will start loading immediately, the router
  // will get the pet_id from the query string and start loading immediately after.
  if (subpath !== undefined && isLoading) {
    return <LoadingView />;
  }

  if (customerApi.error || petApi.error || petsApi.error) {
    // If pet doesn't exist, redirect to remove the pet id.
    if (isNotFoundError(petApi.error)) {
      return (
        <RedirectToPathWithoutPetId subpath={subpath} slug={slug}>
          <LoadingView />
        </RedirectToPathWithoutPetId>
      );
    }

    // Otherwise, render an error
    return <LoadingView variant="failed" />;
  }

  // If the customer is a member already redirect them to confirmation page.
  if (customerApi.isActive && subpath !== CONFIRMATION_SUBPATH) {
    return <Redirect replace to={`/onboarding/${CONFIRMATION_SUBPATH}/`} />;
  }

  // If we're trying to create a new pet, but not all pets have products,
  // redirect to the first pet that doesn't.
  if (
    !isLoading &&
    petId === '' &&
    petsApi.pets &&
    petsApi.pets.length &&
    !allPetsHaveProductInCart(petsApi.pets, cartApi.cart)
  ) {
    const pet = getFirstPetWithNoProductInCart(petsApi.pets, cartApi.cart);
    return (
      <RedirectToPathWithPetId subpath={subpath} slug={slug} pet={pet}>
        <LoadingView />
      </RedirectToPathWithPetId>
    );
  }

  switch (subpath) {
    case undefined:
      const page = getPageonInitialFlow();
      const offerText = offer.formattedAmount
        ? `with ${offer.formattedAmount} off`
        : '';
      return (
        <GetStartedContainer
          continuePath={page}
          continueTextMobile={`Continue ${offerText}`}
          continueTextDesktop={`Continue`}
        />
      );

    case QUIZ_SUBPATH:
      // All quiz steps need a `pet_id` associated with them.
      if (petId == null) {
        return (
          <RedirectToPathWithPetId
            subpath={subpath}
            slug={slug}
            pet={
              petsApi.pets
                ? getFirstPetWithIncompleteSteps(
                    petsApi.pets,
                    customerApi.customer,
                    steps,
                  ) ?? petsApi.pets[0]
                : EMPTY_PET
            }
          >
            <LoadingView />
          </RedirectToPathWithPetId>
        );
      }

      const currentStep = getCurrentStep();
      if (currentStep.slug !== slug) {
        return (
          <Redirect
            replace
            to={`/onboarding/quiz/${currentStep.slug}/?pet_id=${petId}`}
          >
            <LoadingView />
          </Redirect>
        );
      }

      const isFirstStep = currentStep.index === 0;
      const goToNextStep = responseId =>
        router.push(
          getNextStepPath({
            currentId: responseId,
            currentSlug: currentStep.slug,
          }),
        );

      const goForward = async payload => {
        // If we created or updated a pet, flow continues w/ that pet.
        if (payload.model === 'pet') {
          goToNextStep(payload.pet.id);
          return;
        }
        // If we created a customer, `petId` is (still) an empty string.
        if (payload.action === 'create') {
          goToNextStep('');
          return;
        }
        // Handle existing customer scenario
        const {
          data: { results: pets },
        } = await petsApi.listPets();
        // Returning customer never created a pet.
        if (pets.length === 0) {
          goToNextStep('');
          return;
        }
        const cartResult = await cartApi.refreshCart();
        ApiResult.match(cartResult, {
          success: cart => {
            // If all pets have plans, send straight to checkout.
            if (allPetsHaveProductInCart(pets, cart)) {
              router.push(`/onboarding/${CHECKOUT_SUBPATH}/`);
              return;
            }
            const { customer } = payload;
            // If all pets have completed all quiz steps,
            // send to plan selection for first pet w/o plan.
            if (areAllStepsComplete(pets, customer, steps)) {
              router.push(
                `/onboarding/${MEALPREP_SELECTION_SUBPATH}/?pet_id=${
                  pets.find(pet => !pet.has_plan).id
                }`,
              );
              return;
            }
            // Otherwise, send to first incomplete step.
            for (let index = 0; index <= pets.length - 1; index++) {
              const pet = pets[index];
              const nextIncompleteStep = calcCurrentStep(
                steps.length - 1,
                { ...data, pet, customer },
                steps,
                content,
              );
              const isLastPet = index === pets.length - 1;
              if (isLastPet || nextIncompleteStep.slug !== ALLERGIES_SLUG) {
                router.push(
                  `/onboarding/${QUIZ_SUBPATH}/${nextIncompleteStep.slug}/?pet_id=${pet.id}`,
                );
                return;
              }
            }
          },
        });
      };

      const goBack = () => {
        reporter.tag(onboardingBackButtonClicked(currentStep.index));

        router.push(
          !isFirstStep
            ? `/onboarding/quiz/${
                steps[currentStep.index - 1].slug
              }/?pet_id=${petId}`
            : `/onboarding/?pet_id=${petId}`,
        );
      };

      const goToLogin = () => {
        router.push(`/auth/login/`);
        showToast({
          status: 'success',
          message:
            'We found an account associated with your email. Please log in to access your account.',
        });
      };

      return (
        <QuizContainer
          petId={petId}
          goForward={goForward}
          goBack={goBack}
          goToLogin={goToLogin}
          step={currentStep}
          totalSteps={steps.length}
        />
      );

    case MEALPREP_SELECTION_SUBPATH:
    case RECIPES_SELECTION_SUBPATH:
      if (!customerApi.isAuthenticated) {
        return (
          <RedirectToPathWithoutPetId subpath={QUIZ_SUBPATH}>
            <LoadingView />
          </RedirectToPathWithoutPetId>
        );
      }

      // Plan selection also needs a petId
      if (!petId) {
        return (
          <RedirectToPathWithPetId
            subpath={subpath}
            slug={slug}
            pet={
              getFirstPetWithIncompleteSteps(
                petsApi.pets,
                customerApi.customer,
                steps,
              ) ?? petsApi.pets[0]
            }
          >
            <LoadingView />
          </RedirectToPathWithPetId>
        );
      }

      if (!allStepsValid(data, steps)) {
        const currentStep = getCurrentStep();
        return (
          <Redirect
            replace
            to={`/onboarding/quiz/${currentStep.slug}/?pet_id=${petId}`}
          >
            <LoadingView />
          </Redirect>
        );
      }

      const returnToQuiz = () => {
        router.push(
          `/onboarding/quiz/${steps[steps.length - 1].slug}/?pet_id=${petId}`,
        );
      };

      const finishPlanSelection = () => (
        <Redirect to={`/onboarding/checkout/`} />
      );

      const onClickDeletePet = async () => {
        try {
          await petApi.deletePet();
          router.push(`/onboarding/${CHECKOUT_SUBPATH}/`);
        } catch (e) {
          reporter.error(e);
        }
      };
      return (
        <PrefixedReporterProvider prefix="Onboarding">
          <PlanSelectionContainer
            step={slug}
            petId={petId}
            returnToQuiz={returnToQuiz}
            onClickDeletePet={onClickDeletePet}
            skipInterstitial={skipInterstitialRef.current}
            rootPath={ONBOARDING_PATH}
            finishPlanSelection={finishPlanSelection}
            {...translateContentToProps(content)}
          />
        </PrefixedReporterProvider>
      );
    case CHECKOUT_SUBPATH:
      // Checkout should not have a petId
      if (petId != null) {
        return (
          <RedirectToPathWithoutPetId subpath={subpath} slug={slug}>
            <LoadingView />
          </RedirectToPathWithoutPetId>
        );
      }

      // If there are no pets at all, go to the beginning.
      if (!petsApi.pets.length) {
        return <Redirect replace to={`/onboarding/`} />;
      }

      // If there's a pet missing a product, send them to plan selection to get a product.
      // If that pet isn't complete, plan selection will send them to the correct quiz step.
      if (!allPetsHaveProductInCart(petsApi.pets, cartApi.cart)) {
        const pet = getFirstPetWithNoProductInCart(petsApi.pets, cartApi.cart);
        return (
          <Redirect
            replace
            to={`/onboarding/${MEALPREP_SELECTION_SUBPATH}/?pet_id=${pet.id}`}
          />
        );
      }

      return (
        <StripeElementsProvider>
          <PrefixedReporterProvider prefix="Checkout">
            <CheckoutContainer {...translateContentToProps(content)} />
          </PrefixedReporterProvider>
        </StripeElementsProvider>
      );
    case CONFIRMATION_SUBPATH:
      // Neither should confirmation.
      if (petId != null) {
        return (
          <RedirectToPathWithoutPetId subpath={subpath} slug={slug}>
            <LoadingView />
          </RedirectToPathWithoutPetId>
        );
      }

      // If the customer is not active, then send them to the beginning.
      if (!customerApi.isActive) {
        return <Redirect replace to={`/onboarding/`} />;
      }

      return (
        <PrefixedReporterProvider prefix="Confirmation">
          <CheckoutConfirmationContainer
            {...translateContentToProps(content)}
          />
        </PrefixedReporterProvider>
      );
    default:
      throw new Error(`Invalid subpath found: ${subpath}`);
  }
};
OnboardingContainer.displayName = 'OnboardingContainer';
OnboardingContainer.propTypes = {
  content: PropTypes.object.isRequired,
};
